import {
	AccountBalanceWallet,
	Group,
	HourglassEmpty,
	Lock,
	Payments,
	PendingActions,
	Send,
	ShowChart,
	Storefront,
	TrendingUp,
} from '@mui/icons-material';
import { Box, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { DecoratedTitle } from '../../components';
import {
	getIndicacaoConfiguracao,
	getIndicacaoDashboard,
	getIndicacaoEvolucao,
	getIndicacaoEvolucaoRepasse,
	getIndicacaoIndicados,
	getIndicacaoSolicitacaoGanhos,
	getIndicacaoSolicitacoes,
	getIndicacaoUsuarios,
	IndicacaoDashboard,
	IndicacaoEvolucaoMensal,
	IndicacaoFiltros,
	IndicacaoGanhoMensal,
	IndicacaoIndicados,
	IndicacaoLink,
	IndicacaoSolicitacaoGanhos,
	IndicacaoSolicitacaoLista,
	IndicacaoUsuarioBusca,
	postIndicacaoDecisao,
	postIndicacaoLink,
	postIndicacaoSolicitacao,
	putIndicacaoConfiguracao,
	putIndicacaoPagamento,
} from '../../services/indicacao';
import { useToastStore, useUserStore } from '../../stores';
import { UserRole } from '../../utils/enums/UserRole';
import { formatCurrencyBRL } from '../../utils/mask/MaskUtils';
import {
	AceiteDialog,
	ChartCard,
	CommissionPanel,
	FilterToolbar,
	IndicacaoButton,
	IndicadosGrid,
	KpiCard,
	KpiCardSkeleton,
	LinkHero,
	RegraIndicadorPanel,
	SolicitacaoCta,
	SolicitacoesGrid,
} from './components';
import {
	comissaoValida,
	formatPeriodo,
	parseComissao,
	parseQuantidadeOpcional,
	quantidadeOpcionalValida,
} from './indicacao.utils';

const Indicacao: FC = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const primary = theme.palette.primary.main;
	const { setToast } = useToastStore();
	const { user } = useUserStore();
	const isAdmin = user?.grupoAcesso?.id?.toString() === UserRole.Admin;
	const [carregandoDados, setCarregandoDados] = useState(true);
	const [generating, setGenerating] = useState(false);
	const [linkCopiadoId, setLinkCopiadoId] = useState<number | null>(null);
	const [solicitando, setSolicitando] = useState(false);
	const [decidindoId, setDecidindoId] = useState<number | null>(null);
	const [pagandoId, setPagandoId] = useState<number | null>(null);
	const [valorMoto, setValorMoto] = useState('');
	const [valorOutros, setValorOutros] = useState('');
	const [tipoRegraPadrao, setTipoRegraPadrao] = useState('');
	const [qtdDias, setQtdDias] = useState('');
	const [qtdVezes, setQtdVezes] = useState('');
	const [carregandoConfig, setCarregandoConfig] = useState(false);
	const [salvandoConfig, setSalvandoConfig] = useState(false);
	const [aceiteAberto, setAceiteAberto] = useState<IndicacaoSolicitacaoLista | null>(null);
	const [ganhosAceite, setGanhosAceite] = useState<IndicacaoSolicitacaoGanhos | null>(null);
	const [carregandoGanhos, setCarregandoGanhos] = useState(false);
	const [dashboard, setDashboard] = useState<IndicacaoDashboard | null>(null);
	const [evolucao, setEvolucao] = useState<IndicacaoEvolucaoMensal[]>([]);
	const [evolucaoRepasse, setEvolucaoRepasse] = useState<IndicacaoGanhoMensal[]>([]);
	const [solicitacoes, setSolicitacoes] = useState<IndicacaoSolicitacaoLista[]>([]);
	const [indicados, setIndicados] = useState<IndicacaoIndicados>({ lojistas: [], grupos: [] });
	const [dataInicio, setDataInicio] = useState('');
	const [dataFim, setDataFim] = useState('');
	const [usuarioSelecionado, setUsuarioSelecionado] = useState<IndicacaoUsuarioBusca | null>(null);
	const [opcoesUsuario, setOpcoesUsuario] = useState<IndicacaoUsuarioBusca[]>([]);
	const [buscandoUsuario, setBuscandoUsuario] = useState(false);
	const [filtros, setFiltros] = useState<IndicacaoFiltros>({});
	const cargaRef = useRef(0);
	const buscaUsuarioRef = useRef(0);
	const termoUsuarioRef = useRef('');
	const debounceUsuarioRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const copiaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const loadDados = useCallback(async () => {
		const carga = ++cargaRef.current;
		setCarregandoDados(true);
		try {
			const [
				dashboardResponse,
				evolucaoResponse,
				evolucaoRepasseResponse,
				solicitacoesResponse,
				indicadosResponse,
			] = await Promise.all([
				getIndicacaoDashboard(filtros),
				getIndicacaoEvolucao(filtros),
				getIndicacaoEvolucaoRepasse(filtros),
				getIndicacaoSolicitacoes(filtros),
				getIndicacaoIndicados(filtros),
			]);
			if (carga !== cargaRef.current) return;
			if (!dashboardResponse?.success) {
				setToast({
					message: dashboardResponse?.message || 'Erro ao carregar indicação.',
					variant: 'error',
				});
			} else {
				setDashboard(dashboardResponse.data);
			}
			if (!evolucaoResponse?.success) {
				setToast({
					message: evolucaoResponse?.message || 'Erro ao carregar a evolução dos links.',
					variant: 'error',
				});
			} else {
				setEvolucao(evolucaoResponse.data ?? []);
			}
			if (!evolucaoRepasseResponse?.success) {
				setToast({
					message:
						evolucaoRepasseResponse?.message || 'Erro ao carregar os valores repassados.',
					variant: 'error',
				});
			} else {
				setEvolucaoRepasse(evolucaoRepasseResponse.data ?? []);
			}
			if (!solicitacoesResponse?.success) {
				setToast({
					message: solicitacoesResponse?.message || 'Erro ao carregar solicitações.',
					variant: 'error',
				});
			} else {
				setSolicitacoes(solicitacoesResponse.data ?? []);
			}
			if (!indicadosResponse?.success) {
				setToast({
					message: indicadosResponse?.message || 'Erro ao carregar indicados.',
					variant: 'error',
				});
			} else {
				setIndicados(indicadosResponse.data ?? { lojistas: [], grupos: [] });
			}
		} catch {
			if (carga !== cargaRef.current) return;
			setToast({ message: 'Erro ao carregar indicação.', variant: 'error' });
		} finally {
			if (carga === cargaRef.current) setCarregandoDados(false);
		}
	}, [filtros, setToast]);

	useEffect(() => {
		void loadDados();
	}, [loadDados]);

	useEffect(
		() => () => {
			if (debounceUsuarioRef.current) clearTimeout(debounceUsuarioRef.current);
			if (copiaTimeoutRef.current) clearTimeout(copiaTimeoutRef.current);
		},
		[]
	);

	useEffect(() => {
		if (!isAdmin) return;
		let ativo = true;
		setCarregandoConfig(true);
		void getIndicacaoConfiguracao()
			.then((response) => {
				if (!ativo) return;
				if (!response?.success || !response.data) {
					setToast({
						message: response?.message || 'Erro ao carregar a comissão.',
						variant: 'error',
					});
					return;
				}
				setValorMoto(formatCurrencyBRL(response.data.valor_moto));
				setValorOutros(formatCurrencyBRL(response.data.valor_outros));
				setTipoRegraPadrao(response.data.tipo_regra_padrao ?? '');
				setQtdDias(response.data.qtd_dias != null ? String(response.data.qtd_dias) : '');
				setQtdVezes(response.data.qtd_vezes != null ? String(response.data.qtd_vezes) : '');
			})
			.catch(() => {
				if (!ativo) return;
				setToast({ message: 'Erro ao carregar a comissão.', variant: 'error' });
			})
			.finally(() => {
				if (ativo) setCarregandoConfig(false);
			});
		return () => {
			ativo = false;
		};
	}, [isAdmin, setToast]);

	const salvarConfiguracao = async () => {
		const moto = parseComissao(valorMoto);
		const outros = parseComissao(valorOutros);
		if (!comissaoValida(moto)) {
			setToast({
				message: 'Informe a comissão de moto entre R$ 0,00 e R$ 999.999,99.',
				variant: 'error',
			});
			return;
		}
		if (!comissaoValida(outros)) {
			setToast({
				message: 'Informe a comissão de outros entre R$ 0,00 e R$ 999.999,99.',
				variant: 'error',
			});
			return;
		}
		if (tipoRegraPadrao !== '' && tipoRegraPadrao !== 'DIAS' && tipoRegraPadrao !== 'VEZES') {
			setToast({ message: 'O modo padrão deve ser por dias ou pelas primeiras compras.', variant: 'error' });
			return;
		}
		const dias = parseQuantidadeOpcional(qtdDias);
		const vezes = parseQuantidadeOpcional(qtdVezes);
		if (!quantidadeOpcionalValida(dias)) {
			setToast({ message: 'Informe os dias vazio ou maior ou igual a 1.', variant: 'error' });
			return;
		}
		if (!quantidadeOpcionalValida(vezes)) {
			setToast({ message: 'Informe as compras vazio ou maior ou igual a 1.', variant: 'error' });
			return;
		}
		setSalvandoConfig(true);
		try {
			const response = await putIndicacaoConfiguracao({
				valor_moto: moto,
				valor_outros: outros,
				qtd_dias: dias,
				qtd_vezes: vezes,
				tipo_regra_padrao: tipoRegraPadrao || null,
			});
			if (!response?.success) {
				setToast({
					message: response?.message || 'Não foi possível salvar a comissão.',
					variant: 'error',
				});
				return;
			}
			if (response.data) {
				setValorMoto(formatCurrencyBRL(response.data.valor_moto));
				setValorOutros(formatCurrencyBRL(response.data.valor_outros));
				setTipoRegraPadrao(response.data.tipo_regra_padrao ?? '');
				setQtdDias(response.data.qtd_dias != null ? String(response.data.qtd_dias) : '');
				setQtdVezes(response.data.qtd_vezes != null ? String(response.data.qtd_vezes) : '');
			}
			setToast({
				message: response.message || 'Comissão atualizada.',
				variant: 'success',
			});
		} catch {
			setToast({ message: 'Não foi possível salvar a comissão.', variant: 'error' });
		} finally {
			setSalvandoConfig(false);
		}
	};

	const buscarUsuarios = useCallback(
		async (termo: string) => {
			if (!isAdmin) return;
			const carga = ++buscaUsuarioRef.current;
			setOpcoesUsuario([]);
			setBuscandoUsuario(true);
			try {
				const response = await getIndicacaoUsuarios(termo);
				if (carga !== buscaUsuarioRef.current) return;
				setOpcoesUsuario(response?.success ? response.data ?? [] : []);
			} catch {
				if (carga !== buscaUsuarioRef.current) return;
				setOpcoesUsuario([]);
			} finally {
				if (carga === buscaUsuarioRef.current) setBuscandoUsuario(false);
			}
		},
		[isAdmin]
	);

	const agendarBuscaUsuario = (termo: string) => {
		if (debounceUsuarioRef.current) clearTimeout(debounceUsuarioRef.current);
		debounceUsuarioRef.current = setTimeout(() => {
			void buscarUsuarios(termo);
		}, 300);
	};

	const datasInvalidas = () => {
		if (dataInicio && dataFim && dataInicio > dataFim) {
			setToast({
				message: 'A data inicial não pode ser maior que a data final.',
				variant: 'error',
			});
			return true;
		}
		return false;
	};

	const montarFiltros = (usuario: IndicacaoUsuarioBusca | null): IndicacaoFiltros => ({
		data_inicio: dataInicio || undefined,
		data_fim: dataFim || undefined,
		id_usuario: isAdmin && usuario ? usuario.id : undefined,
	});

	const handleDecidir = async (id: number, decisao: 'ACEITA' | 'RECUSADA') => {
		setDecidindoId(id);
		try {
			const response = await postIndicacaoDecisao(id, decisao);
			if (!response?.success) {
				setToast({
					message: response?.message || 'Não foi possível decidir a solicitação.',
					variant: 'error',
				});
				return false;
			}
			setToast({
				message: response.message || 'Decisão registrada.',
				variant: 'success',
			});
			if (decisao === 'ACEITA') {
				setAceiteAberto(null);
				setGanhosAceite(null);
			}
			await loadDados();
			return true;
		} catch {
			setToast({ message: 'Erro ao decidir a solicitação.', variant: 'error' });
			return false;
		} finally {
			setDecidindoId(null);
		}
	};

	const handlePagar = async (id: number, paga: boolean) => {
		setPagandoId(id);
		try {
			const response = await putIndicacaoPagamento(id, paga);
			if (!response?.success) {
				setToast({
					message: response?.message || 'Não foi possível atualizar o pagamento.',
					variant: 'error',
				});
				return;
			}
			setToast({
				message: response.message || (paga ? 'Pagamento registrado.' : 'Pagamento estornado.'),
				variant: 'success',
			});
			await loadDados();
		} catch {
			setToast({ message: 'Erro ao atualizar o pagamento.', variant: 'error' });
		} finally {
			setPagandoId(null);
		}
	};

	const handleAbrirAceite = async (item: IndicacaoSolicitacaoLista) => {
		setAceiteAberto(item);
		setGanhosAceite(null);
		setCarregandoGanhos(true);
		try {
			const response = await getIndicacaoSolicitacaoGanhos(item.id);
			if (!response?.success || !response.data) {
				setAceiteAberto(null);
				setToast({
					message: response?.message || 'Não foi possível carregar os anúncios.',
					variant: 'error',
				});
				return;
			}
			setGanhosAceite(response.data);
		} catch {
			setAceiteAberto(null);
			setToast({ message: 'Erro ao carregar os anúncios.', variant: 'error' });
		} finally {
			setCarregandoGanhos(false);
		}
	};

	const handleFecharAceite = () => {
		if (decidindoId !== null) return;
		setAceiteAberto(null);
		setGanhosAceite(null);
	};

	const handleSolicitar = async () => {
		setSolicitando(true);
		try {
			const response = await postIndicacaoSolicitacao();
			if (!response?.success) {
				setToast({
					message: response?.message || 'Não foi possível solicitar o valor pendente.',
					variant: 'error',
				});
				return;
			}
			setToast({
				message: response.message || 'Solicitação registrada. Aguardando análise.',
				variant: 'success',
			});
			await loadDados();
		} catch {
			setToast({ message: 'Erro ao solicitar valor pendente.', variant: 'error' });
		} finally {
			setSolicitando(false);
		}
	};

	const marcarCopiado = (id: number) => {
		setLinkCopiadoId(id);
		if (copiaTimeoutRef.current) clearTimeout(copiaTimeoutRef.current);
		copiaTimeoutRef.current = setTimeout(() => setLinkCopiadoId(null), 3000);
	};

	const handleGerarLink = async () => {
		setGenerating(true);
		try {
			const origin = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
			const response = await postIndicacaoLink(origin);
			if (!response?.success || !response.data) {
				setToast({
					message: response?.message || 'Não foi possível gerar o link.',
					variant: 'error',
				});
				return;
			}
			await navigator.clipboard.writeText(response.data.url_gerada);
			marcarCopiado(response.data.id);
			setToast({ message: 'Link gerado e copiado.', variant: 'success' });
			await loadDados();
		} catch {
			setToast({ message: 'Erro ao gerar link.', variant: 'error' });
		} finally {
			setGenerating(false);
		}
	};

	const handleCopiar = async (link: IndicacaoLink) => {
		try {
			await navigator.clipboard.writeText(link.url_gerada);
			marcarCopiado(link.id);
			setToast({ message: 'Link copiado.', variant: 'success' });
		} catch {
			setToast({ message: 'Não foi possível copiar o link.', variant: 'error' });
		}
	};

	const aplicarFiltro = () => {
		if (datasInvalidas()) return;
		setFiltros(montarFiltros(usuarioSelecionado));
	};

	const selecionarUsuario = (usuario: IndicacaoUsuarioBusca | null) => {
		if (usuario && datasInvalidas()) return;
		setUsuarioSelecionado(usuario);
		if (!usuario && dataInicio && dataFim && dataInicio > dataFim) {
			setFiltros((atual) => ({ ...atual, id_usuario: undefined }));
			return;
		}
		setFiltros(montarFiltros(usuario));
	};

	const limparFiltro = () => {
		setDataInicio('');
		setDataFim('');
		setUsuarioSelecionado(null);
		setOpcoesUsuario([]);
		termoUsuarioRef.current = '';
		setFiltros({});
	};

	const solicitacao = dashboard?.solicitacao;
	const temPendente = (dashboard?.valor_pendente ?? 0) > 0;
	const podeSolicitar = temPendente && solicitacao?.status !== 'SOLICITADA';
	const periodoFiltrado = Boolean(filtros.data_inicio || filtros.data_fim || filtros.id_usuario);
	const abertaForaDoFiltro =
		solicitacao?.status === 'SOLICITADA' && !solicitacoes.some((item) => item.id === solicitacao.id);
	const linksIndicacao = isAdmin ? [] : dashboard?.links ?? [];
	const linkPrincipal = isAdmin
		? undefined
		: linksIndicacao.find((link) => link.ativo) ?? linksIndicacao[0];
	const outrosLinks = !isAdmin && linkPrincipal
		? linksIndicacao.filter((link) => link.id !== linkPrincipal.id)
		: [];
	const emptySolicitacoes = periodoFiltrado
		? 'Nenhuma solicitação encontrada neste período.'
		: !isAdmin && !temPendente
			? 'Não há valor pendente para solicitar no momento.'
			: 'Nenhuma solicitação encontrada.';

	const kpis = [
		{
			value: formatCurrencyBRL(dashboard?.valor_bloqueado ?? 0),
			label: 'Valor Ganho Esperando Confirmação de Pagamento',
			hint: 'Ainda não liberado',
			icon: <Lock sx={{ fontSize: 20 }} />,
			accent: 'rgb(180, 83, 9)',
		},
		{
			value: formatCurrencyBRL(dashboard?.valor_pendente ?? 0),
			label: 'Valor Disponível para Saque',
			hint: 'Ainda não solicitado',
			icon: <HourglassEmpty sx={{ fontSize: 20 }} />,
			accent: 'rgb(220, 38, 38)',
			action: !isAdmin ? (
				<IndicacaoButton
					label={solicitando ? 'Solicitando...' : 'Solicitar saque'}
					icon={<Send />}
					size="small"
					loading={solicitando}
					disabled={!podeSolicitar}
					onClick={() => void handleSolicitar()}
					fullWidth
				/>
			) : undefined,
		},
		{
			value: formatCurrencyBRL(dashboard?.valor_solicitado ?? 0),
			label: 'Valor em Análise para Saque',
			hint: 'Em análise do administrador',
			icon: <PendingActions sx={{ fontSize: 20 }} />,
			accent: 'rgb(217, 119, 6)',
		},
		{
			value: formatCurrencyBRL(dashboard?.valor_disponivel ?? 0),
			label: 'Valor disponível',
			hint: 'Pronto para saque',
			icon: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
			accent: 'rgb(5, 150, 104)',
		},
		{
			value: formatCurrencyBRL(dashboard?.valor_sacado ?? 0),
			label: 'Valor sacado',
			hint: 'Já pago',
			icon: <Payments sx={{ fontSize: 20 }} />,
			accent: 'rgb(37, 100, 235)',
		},
		{
			value: formatCurrencyBRL(dashboard?.valor_acumulado ?? 0),
			label: 'Total acumulado',
			hint: 'Soma dos ganhos',
			icon: <TrendingUp sx={{ fontSize: 20 }} />,
			accent: primary,
		},
		{
			value: dashboard?.qtd_lojista_indicado ?? 0,
			label: 'Lojistas indicados',
			hint: 'Cadastros pelo link',
			icon: <Storefront sx={{ fontSize: 20 }} />,
			accent: 'rgb(79, 70, 229)',
		},
		{
			value:dashboard?.qtd_grupo_concessionaria_indicado ?? 0,
			label: 'Grupos / concessionárias',
			hint: `${dashboard?.qtd_grupo_concessionaria_indicado ?? 0} ${dashboard?.qtd_grupo_concessionaria_indicado === 1 ? 'concessionária' : 'concessionárias'} vinculado${dashboard?.qtd_grupo_concessionaria_indicado === 1 ? '' : 's'}`,
			icon: <Group sx={{ fontSize: 20 }} />,
			accent: 'rgb(13, 148, 136)',
		},
	];

	return (
		<Box display="flex" flexDirection="column" gap={3} p={isMobile ? 1 : 0}>
			<Box>
				<DecoratedTitle text="Indicação" variant="h2" />
				<Typography fontSize="0.95rem" color="text.secondary" textAlign="center" mt={1}>
					Acompanhe o saldo das indicações, solicite a disponibilização do valor pendente e
					compartilhe seu link.
				</Typography>
			</Box>

			<Box>
				{carregandoDados ? (
					<Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
				) : (
					<LinkHero
						isAdmin={isAdmin}
						totalVisitantes={dashboard?.total_visitantes_links ?? 0}
						totalCadastros={dashboard?.total_cadastros_links ?? 0}
						linkPrincipal={linkPrincipal}
						outrosLinks={outrosLinks}
						linkCopiadoId={linkCopiadoId}
						generating={generating}
						onGerarLink={() => void handleGerarLink()}
						onCopiar={(link) => void handleCopiar(link)}
					/>
				)}
			</Box>

			<Box
				display="grid"
				gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
				gap={2}
			>
				{carregandoDados
					? Array.from({ length: 8 }).map((_, index) => <KpiCardSkeleton key={index} />)
					: kpis.map((kpi) => (
							<KpiCard
								key={kpi.label}
								label={kpi.label}
								value={kpi.value}
								hint={kpi.hint}
								icon={kpi.icon}
								accent={kpi.accent}
								action={'action' in kpi ? kpi.action : undefined}
							/>
					  ))}
			</Box>

			<Box>
				{isAdmin ? (
					<Box display="grid" gap={2}>
						<CommissionPanel
							valorMoto={valorMoto}
							valorOutros={valorOutros}
							tipoRegraPadrao={tipoRegraPadrao}
							qtdDias={qtdDias}
							qtdVezes={qtdVezes}
							carregando={carregandoConfig}
							salvando={salvandoConfig}
							onChangeMoto={setValorMoto}
							onChangeOutros={setValorOutros}
							onChangeTipoRegraPadrao={setTipoRegraPadrao}
							onChangeQtdDias={setQtdDias}
							onChangeQtdVezes={setQtdVezes}
							onSalvar={() => void salvarConfiguracao()}
						/>
						<RegraIndicadorPanel />
					</Box>
				) : carregandoDados ? (
					<Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
				) : (
					<SolicitacaoCta
						valorPendente={formatCurrencyBRL(dashboard?.valor_pendente ?? 0)}
						podeSolicitar={podeSolicitar}
						solicitando={solicitando}
						temPendente={temPendente}
						statusSolicitacao={solicitacao?.status}
						onSolicitar={() => void handleSolicitar()}
					/>
				)}
			</Box>

			<FilterToolbar
				dataInicio={dataInicio}
				dataFim={dataFim}
				onChangeInicio={setDataInicio}
				onChangeFim={setDataFim}
				isAdmin={isAdmin}
				usuarioSelecionado={usuarioSelecionado}
				opcoesUsuario={opcoesUsuario}
				buscandoUsuario={buscandoUsuario}
				usuarioPlaceholder={isAdmin ? 'Nome do usuário' : user?.nome?.trim() ?? ''}
				onOpenUsuario={() => {
					if (isAdmin) void buscarUsuarios(termoUsuarioRef.current);
				}}
				onInputUsuario={(value, reason) => {
					if (!isAdmin || (reason !== 'input' && reason !== 'clear')) return;
					termoUsuarioRef.current = reason === 'clear' ? '' : value;
					setOpcoesUsuario([]);
					if (reason === 'clear') {
						void buscarUsuarios('');
						return;
					}
					agendarBuscaUsuario(value);
				}}
				onChangeUsuario={selecionarUsuario}
				onFiltrar={aplicarFiltro}
				onLimpar={limparFiltro}
			/>

			<Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
				<ChartCard
					icon={<ShowChart />}
					titulo="Ganhos mensais"
					descricao="Evolução dos ganhos no período selecionado."
					labels={(dashboard?.ganhos_mensais ?? []).map((item) => formatPeriodo(item.periodo))}
					datasets={[
						{
							label: 'Ganhos',
							data: (dashboard?.ganhos_mensais ?? []).map((item) => item.valor),
							color: primary,
							fill: true,
						},
					]}
					emptyText="Nenhum ganho mensal no período selecionado."
					formatY={(value) => formatCurrencyBRL(value)}
					loading={carregandoDados}
				/>
				<ChartCard
					icon={<TrendingUp />}
					titulo="Visitas e cadastros"
					descricao="Evolução mensal dos links no período selecionado."
					labels={evolucao.map((item) => formatPeriodo(item.periodo))}
					datasets={[
						{
							label: 'Visitantes',
							data: evolucao.map((item) => item.total_visitantes),
							color: 'rgb(37, 99, 235)',
						},
						{
							label: 'Cadastros',
							data: evolucao.map((item) => item.total_cadastros),
							color: primary,
						},
					]}
					emptyText="Nenhuma visita ou cadastro no período selecionado."
					showLegend
					loading={carregandoDados}
				/>
			</Box>

			<ChartCard
				icon={<Payments />}
				titulo={user?.grupoAcesso?.id===1? "Valores repassados": "Valores Sacados (recebidos)"}
				descricao={user?.grupoAcesso?.id===1? "Soma mensal das solicitações pagas no período selecionado.": "Soma mensal das solicitações sacadas no período selecionado."}
				labels={evolucaoRepasse.map((item) => formatPeriodo(item.periodo))}
				datasets={[
					{
						label: 'Repassado',
						data: evolucaoRepasse.map((item) => item.valor),
						color: 'rgb(5, 150, 104)',
						fill: true,
					},
				]}
				emptyText="Nenhum valor repassado no período selecionado."
				formatY={(value) => formatCurrencyBRL(value)}
				loading={carregandoDados}
			/>

			{carregandoDados ? (
				<Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />
			) : (
				<SolicitacoesGrid
					isAdmin={isAdmin}
					solicitacoes={solicitacoes}
					emptyText={emptySolicitacoes}
					decidindoId={decidindoId}
					pagandoId={pagandoId}
					onAceitar={(item) => void handleAbrirAceite(item)}
					onRecusar={(id) => void handleDecidir(id, 'RECUSADA')}
					onPagar={(id, paga) => void handlePagar(id, paga)}
					alertForaDoFiltro={abertaForaDoFiltro}
					titulo={isAdmin ? 'Solicitações de ganhos' : 'Solicitações'}
					descricao={
						isAdmin
							? 'Analise os pedidos de disponibilização do saldo pendente.'
							: 'Acompanhe os pedidos de disponibilização do saldo pendente.'
					}
				/>
			)}

			{carregandoDados ? (
				<Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />
			) : (
				<IndicadosGrid
					lojistas={indicados.lojistas ?? []}
					grupos={indicados.grupos ?? []}
					periodoFiltrado={periodoFiltrado}
				/>
			)}

			<AceiteDialog
				aberto={aceiteAberto}
				ganhos={ganhosAceite}
				carregando={carregandoGanhos}
				decidindo={decidindoId !== null}
				onFechar={handleFecharAceite}
				onConfirmar={() => aceiteAberto && handleDecidir(aceiteAberto.id, 'ACEITA')}
			/>
		</Box>
	);
};

export default Indicacao;
